import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '@/utils/apiUrl';
import { playNotificationSound, requestNotificationPermission, showBrowserNotification } from '@/utils/notificationService';

const UnreadMessagesContext = createContext();

export const useUnreadMessages = () => {
  const context = useContext(UnreadMessagesContext);
  if (!context) {
    throw new Error('useUnreadMessages must be used within an UnreadMessagesProvider');
  }
  return context;
};

export const UnreadMessagesProvider = ({ children }) => {
  const { token } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const lastUnreadCountRef = useRef(0);
  const lastNotifCountRef = useRef(0);
  const navigate = useNavigate();

  const API = getApiUrl();

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  const fetchUnreadCount = useCallback(async (showNotification = true) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newCount = response.data.unread_count || 0;
      
      if (showNotification && newCount > lastUnreadCountRef.current && lastUnreadCountRef.current >= 0) {
        const newMessages = newCount - lastUnreadCountRef.current;
        if (newMessages > 0) {
          toast.info(`${newMessages} nouveau${newMessages > 1 ? 'x' : ''} message${newMessages > 1 ? 's' : ''}`, {
            duration: 5000,
            action: { label: 'Voir', onClick: () => navigate('/messages') }
          });
          // Play sound
          playNotificationSound();
          // Browser push notification (works in background)
          showBrowserNotification(
            'LCS Immigration - Nouveau message',
            `Vous avez ${newMessages} nouveau${newMessages > 1 ? 'x' : ''} message${newMessages > 1 ? 's' : ''}`,
            () => navigate('/messages')
          );
        }
      }
      
      lastUnreadCountRef.current = newCount;
      setUnreadCount(newCount);
    } catch (error) {
    }
  }, [token, API, navigate]);

  // Also check for new system notifications
  const checkNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications?unread_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const notifs = res.data || [];
      const unreadNotifs = notifs.length;
      if (unreadNotifs > lastNotifCountRef.current && lastNotifCountRef.current >= 0) {
        const newNotifs = unreadNotifs - lastNotifCountRef.current;
        if (newNotifs > 0 && lastNotifCountRef.current > 0) {
          playNotificationSound();
          const latest = notifs[0];
          showBrowserNotification(
            latest?.title || 'LCS Immigration - Notification',
            latest?.message || `${newNotifs} nouvelle${newNotifs > 1 ? 's' : ''} notification${newNotifs > 1 ? 's' : ''}`,
          );
        }
      }
      lastNotifCountRef.current = unreadNotifs;
    } catch (error) {
    }
  }, [token, API]);

  useEffect(() => {
    if (!token) return;
    
    const initialFetch = async () => {
      try {
        const response = await axios.get(`${API}/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const count = response.data.unread_count || 0;
        lastUnreadCountRef.current = count;
        setUnreadCount(count);
      } catch (error) {
      }
      // Init notification count
      try {
        const res = await axios.get(`${API}/notifications?unread_only=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        lastNotifCountRef.current = (res.data || []).length;
      } catch (e) { void 0; }
    };
    initialFetch();

    // Check for new messages and notifications every 15 seconds
    const interval = setInterval(() => {
      fetchUnreadCount(true);
      checkNotifications();
    }, 15000);
    return () => clearInterval(interval);
  }, [token, API, fetchUnreadCount, checkNotifications]);

  const refreshUnreadCount = useCallback(() => {
    fetchUnreadCount(false);
  }, [fetchUnreadCount]);

  const decrementUnreadCount = useCallback((amount = 1) => {
    setUnreadCount(prev => Math.max(0, prev - amount));
    lastUnreadCountRef.current = Math.max(0, lastUnreadCountRef.current - amount);
  }, []);

  const value = useMemo(() => ({
    unreadCount, refreshUnreadCount, decrementUnreadCount, fetchUnreadCount
  }), [unreadCount, refreshUnreadCount, decrementUnreadCount, fetchUnreadCount]);

  return (
    <UnreadMessagesContext.Provider value={value}>
      {children}
    </UnreadMessagesContext.Provider>
  );
};

export default UnreadMessagesContext;
